import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import FeedbackPanel from './feedbackPanel';
import FeedbackTab from './feedbackTab';
import entityComposition from '../common/classes/entityComposition';
import { FEEDBACK_OVERLAY_LABEL } from './feedbackCopy';
import { PROBLEM_CATEGORY_ID } from './feedbackConstants';
import { allowsFeedbackScreenshots, readFeedbackSettings } from './feedbackSettings';
import { buildFeedbackDraft, selectFeedbackForms } from './feedbackFormTypes';
import { buildFeedbackPayload, readDisplayContext } from './feedbackPayload';
import { feedbackFormShape } from './feedbackPropTypes';
import { submitFeedback } from '../../utils/feedbackReporter';
import { installFeedbackDiagnostics, snapshotFeedbackDiagnostics, uninstallFeedbackDiagnostics } from '../../utils/feedbackDiagnostics';
import { captureViewportScreenshot, uploadFeedbackScreenshot } from '../../utils/feedbackScreenshot';

/**
 * The feedback draft lives in the shared `formModal` state. The panel sits above a full-page
 * overlay and the tab is below the Bootstrap modal layer, so a page-owned modal and a feedback
 * draft can never be open at the same time.
 */
const FeedbackWidget = ({
  feedbackEnabled: _feedbackEnabled = false,
  screenshotsAllowed: _screenshotsAllowed = true,
  feedbackForms: _feedbackForms = [],
  formType: _formType = {},
  formTypeActions: _formTypeActions = {},
  formModalActions
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Captured when the panel opens so the context describes the page the user was looking at.
  const [context, setContext] = useState(readDisplayContext);
  const [screenshotBlob, setScreenshotBlob] = useState(null);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const [hasScreenshotError, setHasScreenshotError] = useState(false);

  // Diagnostics are opt-in with the widget: do not instrument every customer at bootstrap.
  useEffect(() => {
    if (!_feedbackEnabled) {
      return undefined;
    }
    installFeedbackDiagnostics();
    return () => {
      uninstallFeedbackDiagnostics();
    };
  }, [_feedbackEnabled]);
  const clearScreenshot = useCallback(() => {
    setScreenshotBlob(null);
    setHasScreenshotError(false);
  }, []);
  const captureScreenshot = useCallback(async () => {
    if (!_screenshotsAllowed) {
      clearScreenshot();
      return;
    }
    setIsCapturingScreenshot(true);
    setHasScreenshotError(false);
    try {
      const blob = await captureViewportScreenshot();
      setScreenshotBlob(blob);
      // Capture returns null when the page cannot be rendered; say so instead of looking idle.
      setHasScreenshotError(!blob);
    } finally {
      setIsCapturingScreenshot(false);
    }
  }, [clearScreenshot, _screenshotsAllowed]);

  // The categories on offer are the feedback form types this account received, so the list is
  // loaded on demand rather than on every page.
  const loadFeedbackForms = useCallback(async () => {
    if (!_formTypeActions.serverIndex || !_formType.indexQuery) {
      return;
    }
    await _formTypeActions.serverIndex(_formType.indexQuery);
  }, [_formTypeActions, _formType.indexQuery]);
  const handleOpen = async () => {
    setContext(readDisplayContext());
    clearScreenshot();
    setIsOpen(true);
    await loadFeedbackForms();
  };
  const handleDiscardDraft = useCallback(() => {
    formModalActions.close();
  }, [formModalActions]);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    clearScreenshot();
  }, [clearScreenshot]);
  const handleRemoveScreenshot = useCallback(() => {
    clearScreenshot();
  }, [clearScreenshot]);
  const handleRetakeScreenshot = useCallback(() => {
    captureScreenshot();
  }, [captureScreenshot]);
  const handleSelectCategory = useCallback(async categoryId => {
    const form = _feedbackForms.find(candidate => candidate.categoryId === categoryId);
    if (!form) {
      return;
    }
    const draft = buildFeedbackDraft(form.format);
    formModalActions.loadDraft(draft);
    // Evaluating the format against the empty draft resolves the behavior-gated sections - the
    // submission details and technical context the server fills in - so they stay hidden here.
    await formModalActions.formatChange(draft.active.id);

    // Capture only for Problem reports, after the panel chrome exists so `.t-feedback` is
    // excluded. Idea / Feedback never hold a page image in memory.
    if (categoryId === PROBLEM_CATEGORY_ID && _screenshotsAllowed) {
      await captureScreenshot();
    } else {
      clearScreenshot();
    }
  }, [captureScreenshot, clearScreenshot, _feedbackForms, formModalActions, _screenshotsAllowed]);
  const handleSubmit = useCallback(async ({
    categoryId,
    values
  }) => {
    let screenshot = {};
    let screenshotAttached = false;
    if (_screenshotsAllowed && categoryId === PROBLEM_CATEGORY_ID && screenshotBlob) {
      const screenshotReference = await uploadFeedbackScreenshot(screenshotBlob);
      screenshot = {
        screenshotReference
      };
      screenshotAttached = true;
    }
    await submitFeedback(buildFeedbackPayload({
      categoryId,
      values,
      context,
      screenshot,
      // Snapshot silently at submit time; never rendered in the panel.
      diagnostics: categoryId === PROBLEM_CATEGORY_ID ? snapshotFeedbackDiagnostics() : ''
    }));
    return {
      screenshotAttached
    };
  }, [context, screenshotBlob, _screenshotsAllowed]);

  // Feedback is opt-in per customer via `feedback.enabled` in customer settings, and the
  // submit endpoint rejects customers that have not enabled it.
  if (!_feedbackEnabled) {
    return null;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "t-feedback"
  }, /*#__PURE__*/React.createElement(FeedbackTab, {
    isPanelOpen: isOpen,
    onOpen: handleOpen
  }), isOpen ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    tabIndex: -1,
    "aria-hidden": "true",
    className: "t-feedback-overlay",
    title: FEEDBACK_OVERLAY_LABEL,
    onClick: handleClose
  }) : null, /*#__PURE__*/React.createElement(FeedbackPanel, {
    isOpen: isOpen,
    onClose: handleClose,
    onSubmit: handleSubmit,
    onSelectCategory: handleSelectCategory,
    onDiscardDraft: handleDiscardDraft,
    context: context,
    forms: _feedbackForms,
    screenshotsAllowed: _screenshotsAllowed,
    screenshotBlob: screenshotBlob,
    isCapturingScreenshot: isCapturingScreenshot,
    hasScreenshotError: hasScreenshotError,
    onRemoveScreenshot: handleRemoveScreenshot,
    onRetakeScreenshot: handleRetakeScreenshot
  }));
};
FeedbackWidget.propTypes = {
  feedbackEnabled: PropTypes.bool,
  screenshotsAllowed: PropTypes.bool,
  feedbackForms: PropTypes.arrayOf(feedbackFormShape),
  formType: PropTypes.shape({
    indexQuery: PropTypes.shape({})
  }),
  formTypeActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }),
  formModalActions: PropTypes.shape({
    close: PropTypes.func.isRequired,
    formatChange: PropTypes.func.isRequired,
    loadDraft: PropTypes.func.isRequired
  }).isRequired
};
export { FeedbackWidget as TestFeedbackWidget };
export default entityComposition(FeedbackWidget, ['formType'], {
  feedbackEnabled: state => {
    var _state$settings;
    return readFeedbackSettings((_state$settings = state.settings) === null || _state$settings === void 0 ? void 0 : _state$settings.settings).enabled;
  },
  screenshotsAllowed: state => {
    var _state$settings2;
    return allowsFeedbackScreenshots(readFeedbackSettings((_state$settings2 = state.settings) === null || _state$settings2 === void 0 ? void 0 : _state$settings2.settings));
  },
  feedbackForms: selectFeedbackForms
}, ['formModal']);